'use client';

import React, { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import {
  initializeData,
  getBrands,
  getSeries,
  getModels,
  getErrorCategories,
  getPriceEntries,
  getFaqs,
  formatEuro,
  Brand,
  Series,
  Model,
  ErrorCategory,
  PriceEntry,
  FaqItem,
} from '@/lib/dataStore';
import { Clock, Shield, Wrench, Mail, ArrowRight, Gamepad2, Settings, MapPin, Star, CheckCircle, Zap, ChevronDown, ChevronLeft, ChevronRight, Award, Handshake, HelpCircle,  } from 'lucide-react';

interface RepairCardData {
  errorCategory: ErrorCategory;
  priceEntry: PriceEntry | undefined;
}

export default function PublicPageClient() {
  const [brands, setBrands] = useState<Brand[]>([]);
  const [allSeries, setAllSeries] = useState<Series[]>([]);
  const [allModels, setAllModels] = useState<Model[]>([]);
  const [allErrorCategories, setAllErrorCategories] = useState<ErrorCategory[]>([]);
  const [allPriceEntries, setAllPriceEntries] = useState<PriceEntry[]>([]);
  const [faqs, setFaqs] = useState<FaqItem[]>([]);
  const [openFaqId, setOpenFaqId] = useState<string | null>(null);

  const [selectedBrandId, setSelectedBrandId] = useState('');
  const [selectedSeriesId, setSelectedSeriesId] = useState('');
  const [selectedModelId, setSelectedModelId] = useState('');

  const [repairCards, setRepairCards] = useState<RepairCardData[]>([]);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [faqSliderIndex, setFaqSliderIndex] = useState(0);
  const FAQ_VISIBLE = 6;

  useEffect(() => {
    initializeData();
    setBrands(getBrands());
    setAllSeries(getSeries());
    setAllModels(getModels());
    setAllErrorCategories(getErrorCategories());
    setAllPriceEntries(getPriceEntries());
    setFaqs(getFaqs());
  }, []);

  const filteredSeries = allSeries.filter(s => s.brandId === selectedBrandId);
  const filteredModels = allModels.filter(m => m.seriesId === selectedSeriesId);

  useEffect(() => {
    if (!selectedModelId) {
      setRepairCards([]);
      return;
    }
    const cats = allErrorCategories.filter(e => e.modelId === selectedModelId);
    const cards: RepairCardData[] = cats.map(cat => ({
      errorCategory: cat,
      priceEntry: allPriceEntries.find(p => p.errorCategoryId === cat.id),
    }));
    setRepairCards(cards);
  }, [selectedModelId, allErrorCategories, allPriceEntries]);

  function handleBrandChange(brandId: string) {
    setSelectedBrandId(brandId);
    setSelectedSeriesId('');
    setSelectedModelId('');
    setRepairCards([]);
  }

  function handleSeriesChange(seriesId: string) {
    setSelectedSeriesId(seriesId);
    setSelectedModelId('');
    setRepairCards([]);
  }

  const selectedBrand = brands.find(b => b.id === selectedBrandId);
  const selectedSeries = allSeries.find(s => s.id === selectedSeriesId);
  const selectedModel = allModels.find(m => m.id === selectedModelId);

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <header className="sticky top-0 z-40 border-b border-border bg-white/90 backdrop-blur-md shadow-sm">
        <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div className="flex items-center gap-3">
              <Image
                src="/assets/images/Media_Rabbit_1_Logo_Zugeschnitten-1782856074259.png"
                alt="Media Rabbit GmbH Logo"
                width={120}
                height={40}
                className="h-9 w-auto object-contain"
                priority
              />
            </div>

            {/* Desktop Nav */}
            <nav className="hidden md:flex items-center gap-1">
              <a href="#preisliste" className="btn-ghost text-sm font-medium">Preisliste</a>
              <Link href="/reparatur-anfrage" className="btn-ghost text-sm font-medium">Anfrage</Link>
              <Link href="/ueber-uns" className="btn-ghost text-sm font-medium">Über uns</Link>
              <a href="#kontakt" className="btn-ghost text-sm font-medium">Kontakt</a>
              <a href="#faq" className="btn-ghost text-sm font-medium">FAQ</a>
            </nav>

            {/* Right side */}
            <div className="flex items-center gap-3">
              <a
                href="#kontakt"
                className="hidden sm:flex btn-primary items-center gap-2 text-sm px-4 py-2"
              >
                <Mail size={14} />
                Reparatur anfragen
              </a>
              <Link
                href="/admin-login"
                className="flex items-center gap-1.5 text-muted-foreground hover:text-foreground transition-colors text-xs px-2 py-1 rounded border border-transparent hover:border-border"
              >
                <Settings size={12} />
                <span className="hidden sm:inline">Admin</span>
              </Link>
              <button
                className="md:hidden btn-ghost p-2"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                aria-label="Menü öffnen"
              >
                <div className="w-5 h-4 flex flex-col justify-between">
                  <span className="block h-0.5 bg-foreground rounded" />
                  <span className="block h-0.5 bg-foreground rounded" />
                  <span className="block h-0.5 bg-foreground rounded" />
                </div>
              </button>
            </div>
          </div>

          {/* Mobile menu */}
          {mobileMenuOpen && (
            <div className="md:hidden border-t border-border py-3 space-y-1 bg-white">
              <a href="#preisliste" className="block btn-ghost text-sm" onClick={() => setMobileMenuOpen(false)}>Preisliste</a>
              <Link href="/reparatur-anfrage" className="block btn-ghost text-sm" onClick={() => setMobileMenuOpen(false)}>Anfrage</Link>
              <Link href="/ueber-uns" className="block btn-ghost text-sm" onClick={() => setMobileMenuOpen(false)}>Über uns</Link>
              <a href="#kontakt" className="block btn-ghost text-sm" onClick={() => setMobileMenuOpen(false)}>Kontakt</a>
              <a href="#faq" className="block btn-ghost text-sm" onClick={() => setMobileMenuOpen(false)}>FAQ</a>
              <a href="#kontakt" className="block btn-primary text-sm text-center mx-3 mt-2" onClick={() => setMobileMenuOpen(false)}>Reparatur anfragen</a>
            </div>
          )}
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative overflow-hidden hero-glow grid-pattern">
        <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-14">
          <div className="flex flex-col items-center text-center">
            {/* Text */}
            <div className="w-full max-w-3xl mx-auto">
              <div className="inline-flex items-center gap-2 bg-primary/8 border border-primary/20 rounded-full px-4 py-1.5 mb-5">
                <Award size={14} className="text-primary" />
                <span className="text-primary text-xs font-semibold tracking-wide">Über 1.000 erfolgreiche Reparaturen</span>
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-foreground mb-5 leading-tight">
                Ihre Konsole in{' '}
                <span className="text-gradient-primary">besten Händen</span>
              </h1>
              <p className="text-secondary-foreground text-lg sm:text-xl max-w-xl mx-auto mb-8 leading-relaxed">
                Professionelle Reparaturen für alle gängigen Spielekonsolen –
                transparent, schnell und mit bis zu 12 Monaten Garantie.
              </p>

              <div className="flex flex-col sm:flex-row gap-3 justify-center mb-8">
                <a
                  href="#preisliste"
                  className="btn-primary flex items-center justify-center gap-2 text-base px-6 py-3"
                >
                  <Gamepad2 size={18} />
                  Preis berechnen
                </a>
                <a
                  href="#kontakt"
                  className="btn-secondary flex items-center justify-center gap-2 text-base px-6 py-3"
                >
                  <Mail size={18} />
                  Kontakt aufnehmen
                </a>
              </div>

              {/* Trust badges */}
              <div className="flex flex-wrap items-center justify-center gap-5 text-sm text-muted-foreground">
                <div className="flex items-center gap-2">
                  <CheckCircle size={15} className="text-success" />
                  <span>Über 1.000 Reparaturen</span>
                </div>
                <div className="flex items-center gap-2">
                  <Star size={15} className="text-warning" />
                  <span>4,9 / 5 Bewertung</span>
                </div>
                <div className="flex items-center gap-2">
                  <Shield size={15} className="text-primary" />
                  <span>12 Monate Garantie</span>
                </div>
              </div>
            </div>
          </div>

          {/* Scroll hint */}
          <div className="flex justify-center mt-10">
            <a href="#preisliste" className="flex flex-col items-center gap-1 text-muted-foreground hover:text-primary transition-colors text-xs">
              <span>Preise entdecken</span>
              <ChevronDown size={18} className="animate-bounce" />
            </a>
          </div>
        </div>
      </section>

      {/* Repair Finder */}
      <section id="preisliste" className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center mb-10">
          <h2 className="text-3xl font-bold text-foreground mb-3">Reparaturpreise finden</h2>
          <p className="text-muted-foreground text-base max-w-lg mx-auto">
            Wählen Sie Ihre Konsole – wir zeigen Ihnen sofort den Festpreis für Ihre Reparatur.
          </p>
        </div>

        {/* 3-Step Selector */}
        <div className="card-surface p-6 mb-10 shadow-card">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
            {/* Step 1: Brand */}
            <div>
              <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                <span className="inline-flex items-center justify-center w-5 h-5 bg-primary/15 text-primary rounded-full text-xs font-bold mr-2">1</span>
                Marke wählen
              </label>
              <div className="relative">
                <select
                  className="select-field"
                  value={selectedBrandId}
                  onChange={e => handleBrandChange(e.target.value)}
                >
                  <option value="">— Marke auswählen —</option>
                  {brands.map(brand => (
                    <option key={`brand-opt-${brand.id}`} value={brand.id}>{brand.name}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Step 2: Series */}
            <div>
              <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                <span className="inline-flex items-center justify-center w-5 h-5 bg-primary/15 text-primary rounded-full text-xs font-bold mr-2">2</span>
                Serie wählen
              </label>
              <select
                className="select-field"
                value={selectedSeriesId}
                onChange={e => handleSeriesChange(e.target.value)}
                disabled={!selectedBrandId}
              >
                <option value="">— Serie auswählen —</option>
                {filteredSeries.map(series => (
                  <option key={`series-opt-${series.id}`} value={series.id}>{series.name}</option>
                ))}
              </select>
            </div>

            {/* Step 3: Model */}
            <div>
              <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                <span className="inline-flex items-center justify-center w-5 h-5 bg-primary/15 text-primary rounded-full text-xs font-bold mr-2">3</span>
                Modell wählen
              </label>
              <select
                className="select-field"
                value={selectedModelId}
                onChange={e => setSelectedModelId(e.target.value)}
                disabled={!selectedSeriesId}
              >
                <option value="">— Modell auswählen —</option>
                {filteredModels.map(model => (
                  <option key={`model-opt-${model.id}`} value={model.id}>{model.name}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Breadcrumb */}
          {selectedBrandId && (
            <div className="flex items-center gap-2 mt-5 pt-4 border-t border-border text-sm text-muted-foreground">
              <span className="text-foreground font-semibold">{selectedBrand?.name}</span>
              {selectedSeriesId && (
                <>
                  <ArrowRight size={14} />
                  <span className="text-foreground font-semibold">{selectedSeries?.name}</span>
                </>
              )}
              {selectedModelId && (
                <>
                  <ArrowRight size={14} />
                  <span className="text-primary font-semibold">{selectedModel?.name}</span>
                </>
              )}
            </div>
          )}
        </div>

        {/* Repair Cards */}
        {selectedModelId && repairCards.length === 0 && (
          <div className="text-center py-16">
            <Wrench size={40} className="text-muted-foreground mx-auto mb-4" />
            <h3 className="text-foreground font-semibold text-lg mb-2">Keine Reparaturen eingetragen</h3>
            <p className="text-muted-foreground text-sm max-w-sm mx-auto">
              Für dieses Modell sind noch keine Preise hinterlegt.
              Kontaktieren Sie uns für ein individuelles Angebot.
            </p>
            <Link href="/reparatur-anfrage" className="btn-primary inline-flex items-center gap-2 mt-5 text-sm px-5 py-2.5">
              <Mail size={14} />
              Anfrage senden
            </Link>
          </div>
        )}

        {repairCards.length > 0 && (
          <div className="animate-slide-up">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-bold text-foreground">
                Reparaturen für{' '}
                <span className="text-primary">{selectedModel?.name}</span>
              </h3>
              <span className="text-muted-foreground text-sm bg-muted px-3 py-1 rounded-full">{repairCards.length} verfügbar</span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {repairCards.map(({ errorCategory, priceEntry }) => {
                const displayPrice = priceEntry ? priceEntry.priceB2C : null;

                return (
                  <div key={`card-${errorCategory.id}`} className="repair-card flex flex-col">
                    <div className="flex items-start justify-between mb-3">
                      <div className="p-2 bg-primary/10 rounded-lg">
                        <Wrench size={18} className="text-primary" />
                      </div>
                      {priceEntry && (
                        <div className="flex items-center gap-1 text-success text-xs font-semibold">
                          <CheckCircle size={12} />
                          <span>Festpreis</span>
                        </div>
                      )}
                    </div>

                    <h4 className="text-foreground font-semibold text-base mb-1 leading-snug">
                      {errorCategory.name}
                    </h4>

                    {errorCategory.description && (
                      <p className="text-muted-foreground text-xs mb-3 leading-relaxed flex-1">
                        {errorCategory.description}
                      </p>
                    )}

                    {priceEntry ? (
                      <div className="mt-auto">
                        <div className="text-3xl font-extrabold text-foreground mb-3 tabular-nums">
                          {displayPrice !== null ? formatEuro(displayPrice) : '–'}
                        </div>

                        <div className="flex items-center gap-4 mb-4 text-xs text-muted-foreground">
                          <div className="flex items-center gap-1.5">
                            <Clock size={13} className="text-accent" />
                            <span>{priceEntry.durationDays} {priceEntry.durationDays === 1 ? 'Tag' : 'Tage'}</span>
                          </div>
                          <div className="flex items-center gap-1.5">
                            <Shield size={13} className="text-success" />
                            <span>{priceEntry.warrantyMonths} {priceEntry.warrantyMonths === 1 ? 'Monat' : 'Monate'} Garantie</span>
                          </div>
                        </div>

                        <Link
                          href={`/reparatur-anfrage?modelId=${selectedModelId}&errorCategoryId=${errorCategory.id}`}
                          className="btn-primary w-full flex items-center justify-center gap-2 text-sm py-2.5"
                        >
                          <Mail size={14} />
                          Reparatur anfragen
                        </Link>
                      </div>
                    ) : (
                      <div className="mt-auto">
                        <p className="text-muted-foreground text-xs mb-3">Preis auf Anfrage</p>
                        <Link
                          href={`/reparatur-anfrage?modelId=${selectedModelId}&errorCategoryId=${errorCategory.id}`}
                          className="btn-secondary w-full flex items-center justify-center gap-2 text-sm py-2.5"
                        >
                          Anfrage senden
                        </Link>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Empty state — no model selected */}
        {!selectedModelId && repairCards.length === 0 && (
          <div className="text-center py-16">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-white border border-border rounded-2xl mb-6 shadow-card">
              <Gamepad2 size={36} className="text-primary/60" />
            </div>
            <h3 className="text-foreground font-semibold text-xl mb-2">Konsole auswählen</h3>
            <p className="text-muted-foreground text-sm max-w-sm mx-auto">
              Wählen Sie oben Marke, Serie und Modell Ihrer Konsole, um alle verfügbaren Reparaturpreise zu sehen.
            </p>
          </div>
        )}
      </section>

      {/* Why Us Section */}
      <section id="warum-wir" className="bg-white border-y border-border py-16">
        <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-foreground mb-3">Warum Media Rabbit?</h2>
            <p className="text-muted-foreground text-base max-w-md mx-auto">
              Wir reparieren Konsolen mit Leidenschaft – seit Jahren vertrauen uns Kunden aus ganz Deutschland.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              {
                icon: Award,
                title: 'Über 1.000 Reparaturen',
                desc: 'Mehr als tausend erfolgreich abgeschlossene Reparaturen sprechen für sich.',
                color: 'text-primary',
                bg: 'bg-primary/8',
              },
              {
                icon: Shield,
                title: 'Bis zu 12 Monate Garantie',
                desc: 'Auf alle Reparaturen gewähren wir eine umfangreiche Garantie.',
                color: 'text-success',
                bg: 'bg-success/8',
              },
              {
                icon: Clock,
                title: 'Schnelle Bearbeitung',
                desc: 'Viele Reparaturen sind bereits in 2–7 Werktagen fertiggestellt.',
                color: 'text-accent',
                bg: 'bg-accent/8',
              },
              {
                icon: Zap,
                title: 'Transparente Festpreise',
                desc: 'Keine versteckten Kosten – Sie sehen den Preis vorab, wir halten ihn ein.',
                color: 'text-warning',
                bg: 'bg-warning/8',
              },
            ].map(item => (
              <div key={`why-${item.title}`} className="card-surface p-6 shadow-card">
                <div className={`inline-flex items-center justify-center w-12 h-12 rounded-xl ${item.bg} mb-4`}>
                  <item.icon size={22} className={item.color} />
                </div>
                <h3 className="text-foreground font-semibold text-base mb-2">{item.title}</h3>
                <p className="text-muted-foreground text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>

          {/* Link to company story page */}
          <div className="flex justify-center mt-8">
            <Link
              href="/ueber-uns"
              className="inline-flex items-center gap-2 text-sm font-semibold text-primary hover:text-primary/80 transition-colors border border-primary/30 hover:border-primary/60 bg-primary/5 hover:bg-primary/10 px-5 py-2.5 rounded-lg"
            >
              <Award size={15} />
              Mehr über uns erfahren – Credentials, Team & B2B-Expertise
              <ArrowRight size={15} />
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ Section — below Warum Media Rabbit */}
      {faqs.length > 0 && (
        <section id="faq" className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 bg-primary/8 border border-primary/20 rounded-full px-4 py-1.5 mb-4">
              <HelpCircle size={14} className="text-primary" />
              <span className="text-primary text-xs font-semibold tracking-wide">Häufige Fragen</span>
            </div>
            <h2 className="text-3xl font-bold text-foreground mb-3">Häufig gestellte Fragen</h2>
            <p className="text-muted-foreground text-base max-w-lg mx-auto">
              Antworten auf die wichtigsten Fragen rund um unsere Reparaturservices.
            </p>
          </div>

          {/* First 6 FAQs — always visible */}
          <div className="max-w-2xl mx-auto space-y-3">
            {faqs.slice(0, FAQ_VISIBLE).map(faq => {
              const isOpen = openFaqId === faq.id;
              return (
                <div key={faq.id} className="card-surface shadow-card overflow-hidden">
                  <button
                    onClick={() => setOpenFaqId(isOpen ? null : faq.id)}
                    className="w-full flex items-center justify-between gap-4 px-6 py-4 text-left hover:bg-muted/40 transition-colors"
                    aria-expanded={isOpen}
                  >
                    <span className="font-semibold text-foreground text-sm leading-snug">{faq.question}</span>
                    <ChevronDown
                      size={18}
                      className={`text-muted-foreground shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
                    />
                  </button>
                  {isOpen && (
                    <div className="px-6 pb-5 pt-1 border-t border-border/60">
                      <p className="text-muted-foreground text-sm leading-relaxed">{faq.answer}</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Slider for remaining FAQs (7+) */}
          {faqs.length > FAQ_VISIBLE && (() => {
            const sliderFaqs = faqs.slice(FAQ_VISIBLE);
            const totalSlides = sliderFaqs.length;
            const currentFaq = sliderFaqs[faqSliderIndex];
            const isOpen = openFaqId === currentFaq?.id;
            return (
              <div className="max-w-2xl mx-auto mt-6">
                <div className="flex items-center gap-2 mb-3">
                  <div className="h-px flex-1 bg-border" />
                  <span className="text-xs text-muted-foreground font-medium px-2">Weitere Fragen</span>
                  <div className="h-px flex-1 bg-border" />
                </div>
                <div className="relative">
                  {/* Slider card */}
                  <div className="card-surface shadow-card overflow-hidden">
                    <button
                      onClick={() => setOpenFaqId(isOpen ? null : currentFaq.id)}
                      className="w-full flex items-center justify-between gap-4 px-6 py-4 text-left hover:bg-muted/40 transition-colors"
                      aria-expanded={isOpen}
                    >
                      <span className="font-semibold text-foreground text-sm leading-snug">{currentFaq.question}</span>
                      <ChevronDown
                        size={18}
                        className={`text-muted-foreground shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
                      />
                    </button>
                    {isOpen && (
                      <div className="px-6 pb-5 pt-1 border-t border-border/60">
                        <p className="text-muted-foreground text-sm leading-relaxed">{currentFaq.answer}</p>
                      </div>
                    )}
                  </div>

                  {/* Slider controls */}
                  <div className="flex items-center justify-between mt-4">
                    <button
                      onClick={() => { setFaqSliderIndex(i => Math.max(0, i - 1)); setOpenFaqId(null); }}
                      disabled={faqSliderIndex === 0}
                      className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground disabled:opacity-30 disabled:cursor-not-allowed transition-colors px-3 py-1.5 rounded-lg border border-border hover:border-primary/40 disabled:hover:border-border"
                    >
                      <ChevronLeft size={16} />
                      Zurück
                    </button>
                    <span className="text-xs text-muted-foreground">
                      {faqSliderIndex + 1} / {totalSlides}
                    </span>
                    <button
                      onClick={() => { setFaqSliderIndex(i => Math.min(totalSlides - 1, i + 1)); setOpenFaqId(null); }}
                      disabled={faqSliderIndex === totalSlides - 1}
                      className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground disabled:opacity-30 disabled:cursor-not-allowed transition-colors px-3 py-1.5 rounded-lg border border-border hover:border-primary/40 disabled:hover:border-border"
                    >
                      Weiter
                      <ChevronRight size={16} />
                    </button>
                  </div>
                </div>
              </div>
            );
          })()}
        </section>
      )}

      {/* Contact Section */}
      <section id="kontakt" className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-foreground mb-3">Kontaktieren Sie uns</h2>
          <p className="text-muted-foreground mb-10 text-base">
            Haben Sie eine Frage oder möchten Sie eine Reparatur anfragen? Wir helfen Ihnen gerne weiter.
          </p>

          <div className="flex justify-center gap-4 mb-10">
            <a
              href="mailto:info@mediarabbit.de"
              className="card-surface p-5 flex flex-col items-center gap-3 hover:border-primary/40 transition-all hover:shadow-md group"
            >
              <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                <Mail size={22} className="text-primary" />
              </div>
              <div>
                <div className="text-foreground font-semibold text-sm">E-Mail</div>
                <div className="text-muted-foreground text-xs">info@mediarabbit.de</div>
              </div>
            </a>
          </div>

          <div className="flex items-center gap-2 justify-center text-muted-foreground text-sm">
            <MapPin size={14} />
            <span>Media Rabbit GmbH · Deutschland</span>
          </div>
        </div>
      </section>

      {/* B2B Section — subtle, for business inquiries only */}
      <section className="bg-foreground text-white py-14">
        <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-4 py-1.5 mb-5">
              <Handshake size={14} className="text-white/80" />
              <span className="text-white/80 text-xs font-semibold tracking-wide">Für Unternehmen & Händler</span>
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold mb-4">Geschäftliche Kooperationen</h2>
            <p className="text-white/70 text-base leading-relaxed mb-6 max-w-xl mx-auto">
              Wir arbeiten bereits erfolgreich mit Unternehmen und Händlern zusammen.
              Für Geschäftskunden bieten wir individuelle Konditionen und maßgeschneiderte Lösungen.
              Melden Sie sich per E-Mail für ein unverbindliches Gespräch.
            </p>
            <a
              href="mailto:info@mediarabbit.de"
              className="inline-flex items-center gap-2 bg-white text-foreground font-semibold px-6 py-3 rounded-lg hover:bg-white/90 transition-colors text-sm"
            >
              <Mail size={16} />
              info@mediarabbit.de
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border bg-white">
        <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <Image
                src="/assets/images/Media_Rabbit_1_Logo_Zugeschnitten-1782856074259.png"
                alt="Media Rabbit GmbH"
                width={90}
                height={30}
                className="h-7 w-auto object-contain"
              />
              <span className="text-muted-foreground text-sm">© 2026 Media Rabbit GmbH</span>
            </div>
            <div className="flex items-center gap-4 text-sm text-muted-foreground">
              <a href="mailto:info@mediarabbit.de" className="hover:text-foreground transition-colors">
                Kontakt
              </a>
              <Link href="/impressum" className="hover:text-foreground transition-colors">
                Impressum
              </Link>
              <Link href="/agb" className="hover:text-foreground transition-colors">
                AGB
              </Link>
              <Link href="/datenschutz" className="hover:text-foreground transition-colors">
                Datenschutz
              </Link>
              <Link href="/widerruf" className="hover:text-foreground transition-colors">
                Widerrufsrecht
              </Link>
              <Link href="/admin-login" className="hover:text-foreground transition-colors text-xs opacity-40">
                Admin
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}